import { useAxios } from "@vueuse/integrations/useAxios";
import { AxiosRequestConfig } from "axios";
import type { Ref } from "vue";
import type { SelectParam } from "../common/types.common";
import { useEnvironmentStore } from "../stores/environment.store";
import { useTokenStore } from "../stores/token.store";
import { RequestController } from "./controller";

export function buildURI(uri: string) {
    const env = useEnvironmentStore();
    return `${env.api}${uri}`;
}

export function get<Res = never>(
    uri: string,
    {
        authenticated,
        responseType,
        downloadable,
    }: {
        authenticated: boolean;
        responseType?: AxiosRequestConfig["responseType"];
        downloadable?: boolean;
    } = {
        authenticated: true,
    },
) {
    let token: string | undefined;
    if (authenticated) {
        token = useTokenStore().token;
    }

    const { data, execute } = useAxios(
        buildURI(uri),
        {
            method: "GET",
            headers: {
                Authorization: token,
            },
            responseType: responseType,
        },
        { immediate: false },
    );

    return new RequestController<Res>(data, execute, downloadable).clearHooks();
}

export function post<Req = never, Res = unknown>(
    uri: string,
    payload: NoInfer<Req>,
    {
        authenticated,
        responseType,
        downloadable,
    }: {
        authenticated: boolean;
        responseType?: AxiosRequestConfig["responseType"];
        downloadable?: boolean;
    } = {
        authenticated: true,
    },
) {
    let token: string | undefined;
    if (authenticated) {
        token = useTokenStore().token;
    }

    const { data, execute } = useAxios(
        buildURI(uri),
        {
            method: "POST",
            headers: {
                Authorization: token,
            },
            data: payload,
            responseType: responseType,
        },
        { immediate: false },
    );

    return new RequestController<Res>(data, execute, downloadable);
}

export function postEmpty<Res = unknown>(
    uri: string,
    options: { authenticated: boolean } = { authenticated: true },
) {
    return post<object, Res>(uri, {}, options);
}

export async function asUpdateHook<T>(
    req: RequestController<T>,
    isLoading: Ref<boolean> | undefined,
) {
    return (await req.standardHooks().execute(isLoading)) !== undefined;
}

export async function asRemoveHook<T>(
    req: RequestController<T>,
    isLoading: Ref<boolean> | undefined,
) {
    return (await req.standardHooks().execute(isLoading)) !== undefined;
}

export function buildSelectParam(param?: SelectParam) {
    if (param === undefined) return "";

    const params = new URLSearchParams();
    if (param.search !== undefined) {
        params.append("search", param.search);
    }
    if (param.current !== undefined && param.current.length > 0) {
        params.append("current", param.current);
    }

    return "?" + params.toString();
}

export function triggerDownload(blobData: Blob | ArrayBuffer, fileName: string) {
    const blob = blobData instanceof Blob ? blobData : new Blob([blobData]);
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
}
